import { Printer, ShareNetwork, WhatsappLogo } from '@phosphor-icons/react';
import { formatINR } from '@/lib/api';
import { useSettings } from '@/context/SettingsContext';
import { toast } from 'sonner';
import { INVOICE } from '@/constants/testIds';

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function buildInvoiceText(order, settings) {
  const items = (order.revised_items || order.items || [])
    .map((it) => `• ${it.name} x${it.quantity} — ${formatINR(it.price * it.quantity)}`)
    .join('\n');
  const total = order.revised_total != null ? order.revised_total : order.total;
  const subtotal = order.revised_subtotal != null ? order.revised_subtotal : order.subtotal;
  const delivery = order.revised_delivery_charge != null ? order.revised_delivery_charge : order.delivery_charge;
  const platform = order.revised_platform_fee != null ? order.revised_platform_fee : (order.platform_fee || 0);
  const addr = order.address || {};
  const store = settings?.store_name || 'MyZippyKart';
  return (
    `*${store} — Order #${order.order_no}*\n\n` +
    `${items}\n\n` +
    `Subtotal: ${formatINR(subtotal)}\n` +
    `Delivery: ${delivery === 0 ? 'FREE' : formatINR(delivery)}\n` +
    `Platform fee: ${formatINR(platform)}\n` +
    `*Total (COD): ${formatINR(total)}*\n\n` +
    `Deliver to: ${addr.name || ''}, ${addr.line1 || ''}${addr.line2 ? ', ' + addr.line2 : ''}, ${addr.city || ''} - ${addr.pincode || ''}\n` +
    `Phone: ${order.customer_phone || ''}\n\n` +
    `Thanks for shopping with us!`
  );
}

function buildInvoiceHTML(order, settings) {
  const items = order.revised_items || order.items || [];
  const total = order.revised_total != null ? order.revised_total : order.total;
  const subtotal = order.revised_subtotal != null ? order.revised_subtotal : order.subtotal;
  const delivery = order.revised_delivery_charge != null ? order.revised_delivery_charge : order.delivery_charge;
  const platform = order.revised_platform_fee != null ? order.revised_platform_fee : (order.platform_fee || 0);
  const discount = order.revised_discount != null ? order.revised_discount : (order.discount || 0);
  const addr = order.address || {};
  const store = settings?.store_name || 'MyZippyKart';
  const phone = settings?.primary_mobile || settings?.store_phone || '';
  const placed = order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : '';

  const itemsHTML = items.map((it) => `
    <tr>
      <td>${escapeHtml(it.name)}</td>
      <td class="r">${it.quantity}</td>
      <td class="r">${formatINR(it.price)}</td>
      <td class="r">${formatINR(it.price * it.quantity)}</td>
    </tr>
  `).join('');

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Invoice ${escapeHtml(order.order_no)} — ${escapeHtml(store)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1c1917;margin:0;padding:24px;background:#fff}
  .wrap{max-width:720px;margin:0 auto}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #15803D;padding-bottom:14px;margin-bottom:18px}
  .brand{font-size:24px;font-weight:800;color:#15803D;letter-spacing:-.5px}
  .muted{color:#78716c;font-size:12px}
  h2{font-size:14px;text-transform:uppercase;letter-spacing:.08em;color:#44403c;margin:18px 0 8px}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th,td{padding:8px 6px;border-bottom:1px solid #e7e5e4;text-align:left}
  th{background:#f5f5f4;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#57534e}
  .r{text-align:right}
  .totals{margin-top:10px;display:flex;justify-content:flex-end}
  .totals table{width:280px}
  .totals td{border:none;padding:4px 0;font-size:13px}
  .grand{font-size:16px;font-weight:800;color:#15803D;border-top:2px solid #15803D !important;padding-top:8px !important}
  .footer{margin-top:28px;padding-top:14px;border-top:1px dashed #d6d3d1;font-size:11px;color:#78716c;text-align:center}
  .badge{display:inline-block;padding:3px 8px;border-radius:999px;background:#dcfce7;color:#15803D;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em}
  @media print { body{padding:0} .noprint{display:none} }
</style></head><body><div class="wrap">
  <div class="head">
    <div>
      <div class="brand">${escapeHtml(store)}</div>
      <div class="muted">Cash on Delivery · ${escapeHtml(settings?.service_area_label || 'India')}</div>
      ${phone ? `<div class="muted">Phone: ${escapeHtml(phone)}</div>` : ''}
    </div>
    <div style="text-align:right">
      <div style="font-weight:700">Invoice</div>
      <div class="muted">#${escapeHtml(order.order_no)}</div>
      <div class="muted">${escapeHtml(placed)}</div>
      <div style="margin-top:6px"><span class="badge">${escapeHtml((order.status || 'placed').replace(/_/g, ' '))}</span></div>
    </div>
  </div>

  <div style="display:flex;gap:24px;flex-wrap:wrap">
    <div style="flex:1;min-width:220px">
      <h2>Bill To</h2>
      <div style="font-size:13px">
        <div style="font-weight:600">${escapeHtml(addr.name || order.customer_name || '')}</div>
        <div>${escapeHtml(order.customer_phone || '')}</div>
        <div class="muted" style="margin-top:4px">
          ${escapeHtml(addr.line1 || '')}${addr.line2 ? ', ' + escapeHtml(addr.line2) : ''}<br/>
          ${escapeHtml(addr.city || '')}, ${escapeHtml(addr.state || '')} - ${escapeHtml(addr.pincode || '')}
          ${addr.landmark ? '<br/>Landmark: ' + escapeHtml(addr.landmark) : ''}
        </div>
      </div>
    </div>
    <div style="flex:1;min-width:220px">
      <h2>Payment</h2>
      <div style="font-size:13px">
        <div><strong>Mode:</strong> Cash on Delivery (COD)</div>
        <div class="muted">Pay the courier when your order arrives.</div>
      </div>
    </div>
  </div>

  <h2>Items</h2>
  <table>
    <thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Amount</th></tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>

  <div class="totals"><table>
    <tr><td>Subtotal</td><td class="r">${formatINR(subtotal)}</td></tr>
    <tr><td>Delivery</td><td class="r">${delivery === 0 ? 'FREE' : formatINR(delivery)}</td></tr>
    <tr><td>Platform fee</td><td class="r">${formatINR(platform)}</td></tr>
    ${discount > 0 ? `<tr><td>Discount</td><td class="r" style="color:#15803D">-${formatINR(discount)}</td></tr>` : ''}
    <tr><td class="grand">Total (COD)</td><td class="r grand">${formatINR(total)}</td></tr>
  </table></div>

  ${order.notes ? `<div style="margin-top:14px;font-size:12px"><strong>Notes:</strong> ${escapeHtml(order.notes)}</div>` : ''}

  <div class="footer">
    Thank you for shopping with ${escapeHtml(store)}. This is a system-generated invoice and does not require a signature.
  </div>

  <div class="noprint" style="margin-top:18px;text-align:center">
    <button onclick="window.print()" style="padding:10px 22px;border-radius:999px;background:#15803D;color:#fff;border:none;font-weight:600;cursor:pointer">Print Invoice</button>
  </div>
</div>
<script>setTimeout(function(){ try { window.focus(); window.print(); } catch(e){} }, 250);</script>
</body></html>`;
}

export default function Invoice({ order, variant = 'customer' }) {
  const { settings } = useSettings();
  if (!order) return null;

  const handlePrint = () => {
    const html = buildInvoiceHTML(order, settings);
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      toast.error('Please allow pop-ups to print the invoice');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleShare = async () => {
    const text = buildInvoiceText(order, settings);
    const shareData = {
      title: `${settings?.store_name || 'MyZippyKart'} — Order #${order.order_no}`,
      text,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Invoice copied to clipboard');
    } catch (e) {
      toast.error('Sharing not supported on this device');
    }
  };

  const handleWhatsApp = () => {
    const text = buildInvoiceText(order, settings);
    const number = (variant === 'admin')
      ? (order.customer_phone || '').replace(/\D/g, '')
      : (settings?.whatsapp_number || '').replace(/\D/g, '');
    const phone = number && number.length >= 10
      ? (number.length === 10 ? `91${number}` : number)
      : '';
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={INVOICE.actions}>
      {variant === 'admin' && (
        <button
          type="button"
          data-testid={INVOICE.printBtn}
          onClick={handlePrint}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-fresh-600 text-white text-sm font-semibold hover:bg-fresh-700 transition-colors"
        >
          <Printer size={16} weight="bold" /> Print
        </button>
      )}
      <button
        type="button"
        data-testid={INVOICE.shareBtn}
        onClick={handleShare}
        className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-stone-300 text-stone-800 text-sm font-medium hover:bg-stone-100 transition-colors"
      >
        <ShareNetwork size={16} weight="bold" /> Share
      </button>
      <button
        type="button"
        data-testid={INVOICE.whatsappBtn}
        onClick={handleWhatsApp}
        className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:brightness-95 transition"
      >
        <WhatsappLogo size={16} weight="fill" /> WhatsApp
      </button>
    </div>
  );
}
